<template>
  <ModalComp
    :isVisible="isVisible"
    @update:isVisible="handleClose"
    @confirm="handleSave"
    title="Modifier l'image"
    size="lg"
  >
    <template #header>
      <h3 class="text-lg font-medium text-gray-900">Lookbook #{{ fiche?.id }}</h3>
    </template>
    
    <template #body>
      <div class="space-y-6">
        <!-- Instructions -->
        <div class="text-sm text-gray-600">
          <p>Pour un affichage optimal de vos lookbook, la dimension de la photo ne doit pas dépasser 1600px en hauteur et en largeur, et doit être inférieur à 2Mo.</p>
        </div>

        <!-- Upload de nouvelle image -->
        <div class="space-y-4">
          <div class="flex items-center gap-4">
            <input 
              ref="fileInput"
              type="file" 
              accept="image/jpeg"
              @change="handleFileChange"
              class="hidden"
            />
            <button 
              type="button"
              @click="fileInput?.click()"
              class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Choisir un fichier
            </button>
            <span class="text-sm text-gray-500">{{ selectedFile ? selectedFile.name : 'Aucun fichier choisi' }}</span>
          </div>
          
          <!-- Aperçu de l'image actuelle -->
          <div v-if="fiche?.image_url" class="space-y-2">
            <label class="text-sm font-medium text-gray-700">Aperçu de l'image actuelle :</label>
            <div class="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <img :src="fiche.image_url" alt="Aperçu" class="max-w-full h-auto rounded" />
            </div>
          </div>
        </div>

        <!-- Informations de la fiche -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <i class="fas fa-info-circle text-blue-600"></i>
              <span class="text-sm font-medium text-blue-900">Informations de la fiche</span>
            </div>
            <span class="text-sm text-gray-600">#{{ fiche?.id }}</span>
          </div>
        </div>

        <!-- Autocomplete produits -->
        <ProductAutocomplete
          v-model="localSelectedProducts"
          label="Liée des références à cette photo de votre lookbook :"
        />

        <!-- Descriptions multilingues -->
        <MultilingualTextarea
          ref="multilingualTextareaRef"
          v-model="localDescriptions"
          title="Description du look :"
        />
      </div>
    </template>
    
    <template #footer>
      <button
        @click="handleClose"
        class="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
      >
        Annuler
      </button>
      <button
        @click="handleSave"
        :disabled="isSaving"
        class="px-4 py-2 bg-blue-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <i v-if="isSaving" class="fas fa-spinner fa-spin mr-2"></i>
        Enregistrer
      </button>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import ModalComp from '@/components/ui/ModalComp.vue'
import ProductAutocomplete from './ProductAutocomplete.vue'
import MultilingualTextarea from './MultilingualTextarea.vue'
import type { LookbookFicheDisplay } from '@/types/lookbook-fiche'
import type { Produit } from '@/types/produit'
import { getFicheProduits } from '@/api/lookbooks'
import { searchProductsByReference } from '@/api/produits'
import { useAuthStore } from '@/stores/auth'

interface Props {
  isVisible: boolean
  fiche: LookbookFicheDisplay | null
  isSaving?: boolean
}

interface Emits {
  (e: 'update:isVisible', value: boolean): void
  (e: 'save', data: { 
    fiche: LookbookFicheDisplay
    file?: File
    products: Produit[]
    descriptions: {
      francais: string
      anglais: string
      espagnol: string
      italien: string
    }
  }): void
}

const props = withDefaults(defineProps<Props>(), {
  isSaving: false
})

const emit = defineEmits<Emits>()

const authStore = useAuthStore()
const fileInput = ref<HTMLInputElement>()
const selectedFile = ref<File | undefined>(undefined)
const multilingualTextareaRef = ref<InstanceType<typeof MultilingualTextarea> | null>(null)

// Variables locales pour v-model
const localSelectedProducts = ref<Produit[]>([])
const localDescriptions = ref({
  francais: '',
  anglais: '',
  espagnol: '',
  italien: ''
})

// Synchroniser avec la fiche quand elle change et charger les produits
watch(() => props.fiche, async (newFiche) => {
  if (newFiche) {
    localDescriptions.value = { ...newFiche.description }
    localSelectedProducts.value = []
    
    // Charger les produits associés
    try {
      const ficheProduits = await getFicheProduits(newFiche.id)
      
      if (ficheProduits.length > 0) {
        const vendeurId = authStore.user?.id_vendeur
        if (vendeurId) {
          const productsPromises = ficheProduits.map(fp => 
            searchProductsByReference(vendeurId, fp.id_produit.toString(), 1)
          )
          const productsResults = await Promise.all(productsPromises)
          localSelectedProducts.value = productsResults
            .filter(results => results.length > 0)
            .map(results => results[0])
        }
      }
    } catch (error) {
      // Erreur silencieuse - les produits de la fiche restent vides
    }
  }
}, { immediate: true })

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    const file = target.files[0]
    
    // Validation du type de fichier
    if (!file.type.startsWith('image/jpeg')) {
      alert('Seuls les fichiers JPEG sont autorisés')
      target.value = ''
      return
    }
    
    // Validation de la taille (2 Mo max)
    const maxSize = 2 * 1024 * 1024
    if (file.size > maxSize) {
      alert('Le fichier est trop volumineux. Taille maximale : 2 Mo')
      target.value = ''
      return
    }
    
    selectedFile.value = file
  }
}

const handleSave = async () => {
  if (!props.fiche) return

  // Auto-traduction de secours : si l'utilisateur a rempli une description
  // mais n'a pas cliqué sur "AI Traduction" (certaines langues vides),
  // on traduit automatiquement avant l'enregistrement.
  if (multilingualTextareaRef.value?.needsAutoTranslate?.()) {
    await multilingualTextareaRef.value.autoTranslate()
  }

  emit('save', {
    fiche: props.fiche,
    file: selectedFile.value,
    products: localSelectedProducts.value,
    descriptions: localDescriptions.value
  })
}

const handleClose = () => {
  emit('update:isVisible', false)
  // Réinitialiser
  selectedFile.value = undefined
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

// Réinitialiser quand le modal se ferme
watch(() => props.isVisible, (newVal) => {
  if (!newVal) {
    selectedFile.value = undefined
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }
})
</script>

